import React from 'react';
import { Icon } from 'antd';
import styled from 'styled-components';

// ---------------------------------------------------------------------------
// Peças visuais das regras de matrícula: blocos ligados por conectores,
// chips, cartões de resultado e a lista que abre embaixo deles. Usadas pela
// regra da tarefa ("Quem recebe esta tarefa") e pela regra do cliente.
// ---------------------------------------------------------------------------

export const say = (n, um, varios) => (n === 1 ? um : varios);

// ── conectores ──────────────────────────────────────────────────────────────

export const Flow = styled.div`
  position: relative;
`;

export const Block = styled.div`
  border: 1px solid ${(p) => (p.muted ? '#f0f0f0' : '#e8e8e8')};
  border-radius: 10px;
  background: ${(p) => (p.muted ? '#fafafa' : '#fff')};
  padding: 12px 14px;
  opacity: ${(p) => (p.muted ? 0.6 : 1)};
`;

export const BlockHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

export const Step = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${(p) => p.bg || '#1890ff'};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
`;

export const Title = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  line-height: 1.3;

  small {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: #8c8c8c;
  }
`;

export const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding-left: 34px;
`;

export const Or = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #bfbfbf;
  text-transform: uppercase;
`;

export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 12px;
  border-radius: 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #595959;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;

  &[aria-pressed='true'] {
    border-color: #1890ff;
    background: #e6f7ff;
    color: #0050b3;
    font-weight: 500;
  }
  &:hover:not(:disabled) { border-color: #1890ff; color: #1890ff; }
  &:disabled { cursor: not-allowed; }
`;

export const AnyChip = styled(Chip)`
  border-style: dashed;
  &[aria-pressed='true'] { border-style: solid; }
`;

export const Connector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 34px;
  padding-left: 13px;

  &::before {
    content: '';
    position: absolute;
    left: 26px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #d9d9d9;
  }
`;

export const Joint = styled.span`
  position: relative;
  z-index: 1;
  min-width: 28px;
  text-align: center;
  padding: 1px 8px;
  border-radius: 10px;
  border: 1px solid #91d5ff;
  background: #fff;
  color: #1890ff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

export const JointNote = styled.span`
  margin-left: 10px;
  font-size: 12px;
  color: #8c8c8c;
`;

export const Sentence = styled.div`
  padding-left: 34px;
  font-size: 13px;
  line-height: 1.5;
  color: #262626;
`;

export const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 8px;
  margin: 12px 0 0 34px;
`;

export const TONES = {
  green: { border: '#b7eb8f', bg: '#f6ffed', fg: '#389e0d' },
  orange: { border: '#ffd591', bg: '#fff7e6', fg: '#d46b08' },
  grey: { border: '#e8e8e8', bg: '#fafafa', fg: '#595959' },
  red: { border: '#ffa39e', bg: '#fff1f0', fg: '#cf1322' },
};

export const Tile = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => TONES[p.tone].border};
  background: ${(p) => TONES[p.tone].bg};
  cursor: pointer;
  transition: box-shadow 0.15s;

  &:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); }
  &[aria-pressed='true'] {
    box-shadow: 0 0 0 2px ${(p) => TONES[p.tone].fg};
  }

  strong {
    font-size: 22px;
    line-height: 1.1;
    color: ${(p) => TONES[p.tone].fg};
  }
  span { font-size: 12px; color: #595959; }
  em {
    margin-top: 4px;
    font-style: normal;
    font-size: 11px;
    color: ${(p) => TONES[p.tone].fg};
  }
  /* Cartão que pede ação: o convite vira botão, para não parecer só um número. */
  &[data-pending='true'] em {
    align-self: flex-start;
    margin-top: 6px;
    padding: 1px 8px;
    border-radius: 10px;
    font-weight: 600;
    color: #fff;
    background: ${(p) => TONES[p.tone].fg};
  }
`;

export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0 0 34px;
  font-size: 12px;
  color: #8c8c8c;
`;

export const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

export const PAGE = 8;

export const Panel = styled.div`
  margin: 12px 0 0 34px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;

  h4 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #262626;
  }
  p {
    margin: 2px 0 0;
    font-size: 12px;
    color: #8c8c8c;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;

  & + & { border-top: 1px solid #f5f5f5; }
`;

export const Who = styled.div`
  flex: 1;
  min-width: 180px;

  div {
    font-size: 13px;
    color: #262626;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  small {
    display: block;
    font-size: 12px;
    color: #8c8c8c;
  }
`;

export const EmptyPanel = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
`;

// Selecionar todas as opções de um recorte é o mesmo que não recortar: a
// regra guarda vazio, que é o "qualquer".
export const toggle = (value, option, options, collapse) => {
  const next = value.indexOf(option) > -1
    ? value.filter((item) => item !== option)
    : value.concat([option]);
  return collapse && next.length === options.length ? [] : next;
};

export const ChoiceBlock = ({ axis, step, title, help, options, value: raw, onChange, anyLabel, disabled, single, separator }) => {
  const sep = separator === undefined ? 'ou' : separator;
  // Regra antiga com todas as opções marcadas aparece como "Qualquer".
  const value = anyLabel && raw.length === options.length ? [] : raw;
  return (
  <Block muted={disabled} data-axis={axis}>
    <BlockHead>
      <Step bg={disabled ? '#bfbfbf' : undefined}>{step}</Step>
      <Title>
        {title}
        <small>{help}</small>
      </Title>
    </BlockHead>
    <Options>
      {anyLabel && (
        <AnyChip
          type="button"
          aria-pressed={value.length === 0}
          disabled={disabled}
          data-chip={anyLabel}
          onClick={() => onChange([])}
        >
          {value.length === 0 && <Icon type="check" />}
          {anyLabel}
        </AnyChip>
      )}
      {options.map((option, index) => {
        const on = value.indexOf(option) > -1;
        return (
          <React.Fragment key={option}>
            {index > 0 && sep && <Or>{sep}</Or>}
            <Chip
              type="button"
              aria-pressed={on}
              disabled={disabled}
              data-chip={option}
              onClick={() => onChange(single ? [option] : toggle(value, option, options, !!anyLabel))}
            >
              {on && <Icon type="check" />}
              {option}
            </Chip>
          </React.Fragment>
        );
      })}
    </Options>
  </Block>
  );
};

export const Joiner = ({ note }) => (
  <Connector>
    <Joint>E</Joint>
    {note && <JointNote>{note}</JointNote>}
  </Connector>
);

